import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { MoreIcon } from './icons.js';

export interface OverflowMenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
}

/**
 * The phone-width home for top-bar controls that do not fit. Anchored inside the
 * sticky header, which is a positioned element and carries no transform, so an
 * absolutely-positioned child resolves against it — no portal needed.
 *
 * Implements the WAI-ARIA menu-button keyboard contract implied by role="menu" /
 * role="menuitem": opening moves focus to the first item (or the last, if opened
 * via ArrowUp), ArrowUp/ArrowDown move between items and wrap, Home/End jump to
 * the ends, Escape closes and restores focus to the trigger, and focus leaving
 * the menu entirely (Tab, click elsewhere) closes it too.
 */
export function OverflowMenu({ items, className }: { items: OverflowMenuItem[]; className?: string }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const focusLastOnOpen = useRef(false);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;

    // Move focus into the menu once it mounts: first item by default, last item
    // if opened via ArrowUp on the trigger.
    const startIndex = focusLastOnOpen.current ? items.length - 1 : 0;
    focusLastOnOpen.current = false;
    itemRefs.current[startIndex]?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
    }
    // mousedown, not pointerdown: jsdom's pointer-event support is uneven and a
    // tap on iOS Safari synthesises mousedown anyway.
    function onOutsideMouse(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    // Keyboard users who Tab (or otherwise move focus) past the last item never
    // fire a mousedown outside — only a focusout with the new focus target
    // outside the wrapper tells us the menu should close.
    function onFocusOut(e: FocusEvent) {
      const next = e.relatedTarget as Node | null;
      // iOS Safari does not focus a <button> on tap (unlike Chrome/Android), so
      // tapping a menu item blurs the auto-focused item to nowhere: relatedTarget
      // is null. Treating that as "focus left the menu" would close the menu
      // before the tap's click event fires, silently swallowing every tap. Only
      // close when focus demonstrably moved to something outside the wrapper.
      if (next && !wrapRef.current?.contains(next)) setOpen(false);
    }
    const wrap = wrapRef.current;
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onOutsideMouse);
    wrap?.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onOutsideMouse);
      wrap?.removeEventListener('focusout', onFocusOut);
    };
  }, [open, items.length]);

  if (items.length === 0) return null;

  function onTriggerKeyDown(e: ReactKeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusLastOnOpen.current = false;
      setOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusLastOnOpen.current = true;
      setOpen(true);
    }
  }

  function onItemKeyDown(e: ReactKeyboardEvent<HTMLButtonElement>, index: number) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        itemRefs.current[(index + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        itemRefs.current[(index - 1 + items.length) % items.length]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        itemRefs.current[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        itemRefs.current[items.length - 1]?.focus();
        break;
    }
  }

  return (
    <div ref={wrapRef} className={`relative ${className ?? ''}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        className="p-2 rounded-lg text-ink-300 hover:text-white hover:bg-white/10 transition"
      >
        <MoreIcon />
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 top-full mt-1 min-w-[11rem] py-1 z-50 rounded-lg
                     bg-canvas-darker ring-1 ring-white/10 shadow-chrome animate-fade-in"
        >
          {items.map((item, index) => (
            <button
              key={item.key}
              ref={(el) => { itemRefs.current[index] = el; }}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); triggerRef.current?.focus(); item.onSelect(); }}
              onKeyDown={(e) => onItemKeyDown(e, index)}
              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-[0.85rem]
                         text-ink-100 hover:bg-white/10 transition"
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
